import React, { useEffect, useState, useRef } from "react";
import { LoaderCircle } from "lucide-react";
import MilaAiPage from "./specialized/MilaAiPage";
import apiClient from "../business/network/ApiClient";
import { CurrentUser } from "../business/helper/CurrentUser";
import { FavoritesList } from "./specialized/FavoritesList";
import { MilaHistoricList } from "./specialized/MilaHistoricList";
import { toast } from "react-toastify";
import MessageNotification from "./specialized/MessageNotification";
import { GrtAnalyticsGA4 } from "./specialized/GrtAnalyticsGA4";
import icon from "../images/logo.svg";
import greenRoadLogo from "../images/greenroad-logo.svg";
import ellipse from "../images/icons/ellipse.svg";
import buttonImage from "../images/icons/button.png";
import Button from "./design/Shared/Button";
import Tooltip from "./design/Shared/Tooltip";
import Scheduled from "./design/Query/Scheduled";
import clsx from "clsx";
import Menu from "./design/Shared/Sidebar/Menu";
import { isMenuDisabled } from "../business/helper/Singleton";
import { useMilaContext } from "./context/useMilaContext";
import TermsAcceptanceModal from "./specialized/TermsAcceptanceModal";
import SessionWatcher from "./SessionWatcher";
import { useOnboarding, ONBOARDING_STATUS } from "./context/OnboardingContext";
import useAccessStamp from "../hooks/useAccessStamp";

const MilaContainer = ({
  handleLogout,
  isFromCentral = false,
  // The shell's rail carries the Central / sign-out entry when it is on screen; it registers
  // for this exact flow (reset the conversation, then log out) rather than a bare logout.
  registerSignOut,
  // The same rail carries the GreenRoad logo and the door to the profile settings, so this
  // component keeps its own floating Central button and its sidebar footer (logo + gear) only
  // where no rail exists — mobile, and a user entitled to no agents.
  hasRail = false,
  // The profile settings are one profile for the whole product, so the shell owns the modal;
  // the gear here is just another door to it, and wears the shell's post-onboarding pulse.
  onOpenSettings,
  isSettingsHighlighted = false,
}) => {
  const [favorites, setFavorites] = useState(null);
  const [milaHistoricData, setMilaHistoricData] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [externalMessage, setExternalMessage] = useState(null);
  const [signOut, setSignOut] = useState(false);
  const [scheduledData, setScheduledData] = useState([]);
  // "loading" | "ready" | "error": what the Scheduled Tasks panel says while its list is empty
  const [scheduledStatus, setScheduledStatus] = useState("loading");
  const [userId, setUserId] = useState(null);
  const [histoConversation, setHistoConversation] = useState(null);
  const [reset, setReset] = useState(false);
  const [resetRequestId, setResetRequestId] = useState(0);
  const [loadingFavorite, setLoadingFavorite] = useState(false);
  const [loadingHisto, setLoadingHisto] = useState(false);
  const [isDisabled, setIsDisabled] = useState(isMenuDisabled.state);
  const [threadId, setThreadId] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [isRightMenuOpen, setIsRightMenuOpen] = useState(false);
  const uploaderResetRef = useRef(null);
  const [refreshNotif, setRefreshNotif] = useState(0);
  const [files, setFiles] = useState([]);
  const milaContext = useMilaContext();
  // Here rather than in the shell: the chat stays mounted behind every agent, so this is the
  // one component every signed-in screen has, rail or no rail.
  useAccessStamp();
  const [showTermsModal, setShowTermsModal] = useState(false);

  const [termsStatus, setTermsStatus] = useState(null);
  const [showNotif, setShowNotif] = useState(false);
  const [notif, setNotif] = useState(null);
  const [currentMessageInBox, setCurrentMessageInBox] = useState(null);

  const onboarding = useOnboarding();
  const onboardingAutoStartRef = useRef(false);
  // The chat surface renders only once the landing destination (normal home vs. onboarding
  // Introduction) is decided — the user never sees the home page flash before a redirect.
  const [landingResolved, setLandingResolved] = useState(false);

  // Eligible (new) users land directly in the onboarding "Introduction" (design page 1):
  // Mila's intro message streams into the chat surface with the consent chips
  // ("Yes, let's go!" / "Not now") in-conversation — no pre-chat prompt.
  const landInOnboardingIfEligible = async () => {
    try {
      if (onboardingAutoStartRef.current) return;
      const data = await onboarding.refresh();
      if (data?.status === ONBOARDING_STATUS.NotStarted) {
        onboardingAutoStartRef.current = true;
        const result = await onboarding.startOnboarding();
        if (result?.threadId) {
          setThreadId(result.threadId);
        }
        analytics?.trackEventAnalytics("Onboarding", "IntroductionLanding", "auto");
      }
    } catch (error) {
      console.error("Onboarding auto-start failed:", error);
    }
  };

  const checkTermsStatus = async () => {
    try {
      const userData = await CurrentUser.GetCurrentUserData();
      if (userData) {
        const data = await apiClient.getTermsAcceptanceStatus(userData.userId);
        setTermsStatus(data);
        setShowTermsModal(!data.hasAcceptedTerms);
        if (data.hasAcceptedTerms) {
          // Onboarding guard runs strictly AFTER the terms gate resolves to "accepted" —
          // a user who hasn't accepted terms never enters onboarding (design §5.1).
          // Awaited so the landing destination is known BEFORE the surface renders.
          await landInOnboardingIfEligible();
        }
      }
    } catch (error) {
      console.error('Error checking terms status:', error);
      toast.error('Failed to check terms acceptance status');
    } finally {
      setLandingResolved(true);
    }
  };

  const handleAcceptTerms = async () => {
    try {
      const result = await apiClient.acceptTerms(userId);
      if (result.success) {
        toast.success('Terms accepted successfully!');
        setTermsStatus({
          hasAcceptedTerms: true,
          acceptedAt: result.acceptedAt
        });
        // Terms just accepted → decide the landing BEFORE dropping the modal, so the
        // user goes straight to the onboarding Introduction (no home page in between).
        await landInOnboardingIfEligible();
        setShowTermsModal(false);
      }
    } catch (error) {
      console.error('Error accepting terms:', error);
      toast.error('Failed to accept terms. Please try again.');
      throw error;
    }
  };

  const handleCancelTerms = async () => {
    await apiClient.cancelTerms(userId);

    setTimeout(() => {
      toast.info('You must accept the terms to continue using AskMila™');
      setTimeout(() => {
        setReset(true);
        setSignOut(true);
        analytics?.trackEventAnalytics("Terms", "CancelExit", "click");
      }, 3500);
    }, 100);
  };

  const handleAnalytics = (ga) => {
    if (ga) {
      setAnalytics(ga);
    }
  };

  const notify = (m) => toast.success(m);

  const onDeleteFavorite = async (id) => {
    let result = await apiClient.deleteFavorite(id);
    if (result === true) {
      await getFavorites();
      notify("The favorite has been deleted.");
      analytics?.trackEventAnalytics("FavoriteMenu", "DeleteFavorite", "click");
    }
  };

  const onConversationLoaded = () => {
    setHistoConversation(null);
  };

  const onFavoriteClick = (title) => {
    setExternalMessage(title);
    setIsSidebarOpen(false);
    analytics?.trackEventAnalytics("SideBar", "OpenFavorite", "click");
  };

  const onHistoricItemClick = async (histo, inputThreadIdFromScheduler) => {
    const response = histo.response;
    const id = histo.threadId || inputThreadIdFromScheduler;
    const charts = histo.charts;
    const histoThreadId = histo.threadId;

    if (uploaderResetRef.current
      && files && Object.values(files).length > 0) {
      uploaderResetRef.current();
      setFiles([]);
    }

    if (response && response.length > 0) {
      response.forEach(element => {
        if (element?.uploadedFiles && element?.uploadedFiles.length > 0
          && milaContext?.uploadedFiles) {
          element?.uploadedFiles?.forEach(file => {
            if (milaContext.uploadedFiles.findIndex(f => f.file_id === file.file_id && f.thread_id === file.thread_id) === -1) {
              milaContext?.uploadedFiles.push(file);
            }
          });
        }
      });
    }
    if (favorites && response && response.length > 0) {
      response.forEach((resp) => {
        if (resp.uploadedFiles && resp.uploadedFiles.length > 0) {
          resp.isFavorite = false;

          for (const uploadedFile of resp.uploadedFiles) {
            const matchingFavorite = favorites.find(favorite =>
              favorite.title &&
              uploadedFile.file_name &&
              favorite.title.includes(uploadedFile.file_name)
            );

            if (matchingFavorite) {
              resp.isFavorite = true;
              break;
            }
          }
        } else {
          resp.isFavorite = false;
        }
      });
    }

    if (charts && Array.isArray(charts)) {
      response.charts = charts;
    }

    if (response?.tables === null || response?.tables === undefined) {
      //Check if some table datas exist we add it to the historic data object
      let idh = histo?.id;
      if (idh) {
        let tableDatas = await apiClient.getMilaHistoricTableDatas(idh);
        if (tableDatas && tableDatas.length > 0) {
          response.tables = tableDatas;
        }
      }
    }

    if (histoThreadId) {
      response.threadId = histoThreadId;
    }

    setResetRequestId((prev) => prev + 1);
    setHistoConversation(response || histo);
    setThreadId(id);
    setRefreshNotif((prev) => prev + 1);
    setIsSidebarOpen(false);

    // Re-entry into the onboarding thread from history: reactivate + "welcome back" turn.
    // Must run AFTER the historic conversation is loaded into the page — the resume turn
    // streams into a fresh pending entry appended to it, never into a persisted turn.
    if (
      id &&
      id === onboarding.onboardingThreadId &&
      [ONBOARDING_STATUS.InProgress, ONBOARDING_STATUS.Declined,
       ONBOARDING_STATUS.Skipped, ONBOARDING_STATUS.Abandoned].includes(onboarding.status)
    ) {
      await onboarding.resumeOnboarding();
    }
    analytics?.trackEventAnalytics("SideBar", "OpenHistory", "click");
  };

  const onRefreshFavorites = async () => {
    let userData = CurrentUser.GetCurrentUserData();
    let favorites = await apiClient.getFavorites(userData?.userId);
    setFavorites(favorites);
  };

  const getFavorites = async () => {
    let userData = CurrentUser.GetCurrentUserData();
    let favorites = await apiClient.getFavorites(userData?.userId);
    setFavorites(favorites);
  };

  const getMilaHistoric = async () => {
    let userData = CurrentUser.GetCurrentUserData();
    let data = await apiClient.getMilaHistoric(userData?.userId);
    setMilaHistoricData(data);
  };

  useEffect(() => {
    const process = async () => {
      // Landing decision first (terms → onboarding eligibility), then the rest
      await checkTermsStatus();
      //Setting the first threadId — unless onboarding took over the surface
      let tId = await apiClient.createThreadId();
      if (tId && !onboardingAutoStartRef.current) {
        setThreadId(tId);
      }
      setLoadingFavorite(true);
      await getFavorites();
      setLoadingFavorite(false);
      setLoadingHisto(true);
      await getMilaHistoric();
      setLoadingHisto(false);
    };
    process();
  }, []);

  useEffect(() => {
    if (milaContext) {
      const fetchRootOu = async () => {
        let udata = await CurrentUser.GetCurrentUserData();
        milaContext.rootOu = await apiClient.getRootOuId(udata?.ouId);
      }
      fetchRootOu();
    }
  }, [milaContext]);

  const handleNewDiscussion = async () => {
    if (isDisabled === false) {
      // "New Chat" during onboarding = skip: flushes whatever was captured so far
      // (single SetMemory call, backend-side) and leaves the thread in history (design §14).
      // Only counts as "leaving onboarding" when the user is actually on that thread —
      // starting a new chat from a normal thread must not end an onboarding sitting in history.
      if (onboarding.isActiveThread(threadId)) {
        await onboarding.skipOnboarding();
        analytics?.trackEventAnalytics("Onboarding", "SkippedViaNewChat", "click");
      }
      setReset(true);
      setResetRequestId((prev) => prev + 1);
      if (files && externalMessage === null) {
        for (const file of Object.values(files)) {
          if (file && file.fileResponse?.file_id) {
            apiClient.deleteUploadedFile(file.fileResponse.file_id, threadId, 'thread');
          }
        }

        if (uploaderResetRef) {
          uploaderResetRef.current();
        }

        if (milaContext?.uploadedFiles && milaContext?.uploadedFiles.length > 0) {
          milaContext.uploadedFiles = [];
        }
      }

      let tId = await apiClient.createThreadId();
      if (tId) {
        setThreadId(tId);
      }
      else {
        setThreadId(null);
      }
      setIsSidebarOpen(false);
      analytics?.trackEventAnalytics("SideBar", "OpenNewDiscussion", "click");
    }
  };

  const onExternalMessageHandled = async () => {
    setExternalMessage(null);
  };

  const onResetDone = async () => {
    setReset(false);
    setThreadId(null);
    if (signOut === true) {
      handleLogout();
    }
  };

  const onSignOut = () => {
    setReset(true);
    setSignOut(true);
    analytics?.trackEventAnalytics("SideBar", "SignOut", "click");
  };

  // The rail's sign-out entry runs this same flow. Registered through a latest-ref so the
  // shell holds one stable function while the flow underneath keeps seeing current state
  // (`analytics` arrives after mount).
  const onSignOutRef = useRef(null);
  onSignOutRef.current = onSignOut;

  useEffect(() => {
    registerSignOut?.(() => onSignOutRef.current?.());
  }, [registerSignOut]);

  const handleMessageSelected = (m) => {
    setNotif(m);
    setShowNotif(true);
    analytics?.trackEventAnalytics(
      "SideBar",
      "OpenMessageNotificationDetails",
      "click"
    );
  };

  const updateScheduledData = async (data) => {
    setScheduledData(data);
    const userData = await CurrentUser.GetCurrentUserData();
    setUserId(userData?.userId);
  };

  useEffect(() => {
    const event = () => setIsDisabled(isMenuDisabled.state);
    isMenuDisabled.subscribe(event);

    return () => {
      isMenuDisabled.unsubscribe(event);
    };
  }, []);

  const handleFilesUploaded = async (files) => {
    setFiles(files);
  }

  return (
    <div
      className="h-screen w-full flex flex-1"
      style={{ background: "var(--color-whitesmoke)" }}
    >
      <Tooltip id="tooltipHisto" />
      <Tooltip id="tooltipFavorite" />
      <Tooltip id="tooltipTasks" />

      <aside
        className={clsx(
          "flex flex-col items-center overflow-hidden transition-all duration-300 bg-whitesmoke",
          "fixed -left-full z-20 h-full lg:relative sm:-left-[304px] lg:left-0",
          "w-full sm:!w-[304px]",
          "pt-[166px] pb-8 px-2 lg:pb-8 lg:pl-6 lg:pr-0",
          { "!left-0": isSidebarOpen }
        )}
      >
        <div
          className={clsx(
            "fixed top-0 lg:!w-[304px] duration-300 flex flex-col gap-4 items-center py-8 px-6"
          )}
        >
          <h1 className="text-dark-blue-100 text-center font-urbanist text-[32px] font-extrabold leading-[38px]">
            <svg width="156" height="53" viewBox="0 0 63 11" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3.49541 0.938074H5.8211L9.31651 10.5711H7.12844L6.57798 9.05734H2.73853L2.18807 10.5711H0L3.49541 0.938074ZM3.49541 6.99312H5.8211L4.65138 3.80046L3.49541 6.99312Z" fill="#303C54" />
              <path d="M11.9867 10.695C11.638 10.6628 11.3032 10.5872 10.9821 10.4679C10.661 10.3486 10.3743 10.1858 10.122 9.97936C9.86969 9.77294 9.67015 9.52064 9.52337 9.22248L11.2573 8.47936C11.3032 8.52982 11.3674 8.59404 11.45 8.67202C11.5325 8.74541 11.6357 8.81193 11.7596 8.87156C11.888 8.93119 12.0417 8.96101 12.2206 8.96101C12.3399 8.96101 12.4546 8.94954 12.5646 8.92661C12.6793 8.89908 12.7711 8.85321 12.8399 8.78899C12.9133 8.72477 12.95 8.63532 12.95 8.52064C12.95 8.3922 12.9018 8.29587 12.8055 8.23165C12.7137 8.16284 12.6013 8.11468 12.4683 8.08716C12.3353 8.05505 12.2114 8.02982 12.0968 8.01147C11.6885 7.94725 11.2963 7.81881 10.9202 7.62615C10.5486 7.4289 10.2435 7.17431 10.005 6.86239C9.76648 6.54587 9.64722 6.17661 9.64722 5.75459C9.64722 5.28211 9.76878 4.87385 10.0119 4.52982C10.255 4.18578 10.5715 3.92202 10.9614 3.73853C11.3559 3.55046 11.7802 3.45642 12.2344 3.45642C12.7711 3.45642 13.2665 3.56651 13.7206 3.7867C14.1793 4.00688 14.5555 4.32339 14.8491 4.73624L13.2252 5.69954C13.1702 5.63073 13.1013 5.56193 13.0188 5.49312C12.9408 5.41972 12.8491 5.3578 12.7435 5.30734C12.6426 5.25229 12.5325 5.21789 12.4133 5.20413C12.2527 5.19037 12.1082 5.19725 11.9798 5.22477C11.8559 5.24771 11.7573 5.29817 11.6839 5.37615C11.6105 5.44954 11.5738 5.55734 11.5738 5.69954C11.5738 5.83257 11.638 5.9289 11.7665 5.98853C11.8949 6.04817 12.0394 6.09174 12.2 6.11927C12.3605 6.14679 12.4913 6.17661 12.5922 6.20872C12.9867 6.33716 13.3582 6.50459 13.7069 6.71101C14.0555 6.91743 14.3353 7.16743 14.5463 7.46101C14.7619 7.75459 14.8628 8.09862 14.8491 8.49312C14.8491 8.95183 14.7114 9.3555 14.4362 9.70413C14.161 10.0528 13.8055 10.3165 13.3697 10.4954C12.9339 10.6743 12.4729 10.7408 11.9867 10.695Z" fill="#303C54" />
              <path d="M22.5764 10.5711H20.1957L18.8196 8.20413L18.0764 9.08486V10.5711H16.0122V0.25H18.0764V5.87844L19.9067 3.69037H22.604L20.237 6.52523L22.5764 10.5711Z" fill="#303C54" />
              <path d="M23.6993 10.5711L25.5708 0.938074H27.3323L29.4929 6.62156L31.6534 0.938074H33.4149L35.2864 10.5711H33.1809L32.1764 5.36927L30.2085 10.5711H28.7773L26.8094 5.36927L25.8048 10.5711H23.6993Z" fill="#303C54" />
              <path d="M36.5872 3.69037H38.6514V10.5711H36.5872V3.69037ZM37.6331 2.97477C37.3349 2.97477 37.0849 2.87615 36.8831 2.6789C36.6813 2.48165 36.5803 2.23624 36.5803 1.94266C36.5803 1.65367 36.6813 1.41055 36.8831 1.2133C37.0849 1.01147 37.3326 0.910551 37.6262 0.910551C37.9152 0.910551 38.1583 1.01147 38.3556 1.2133C38.5574 1.41055 38.6583 1.65367 38.6583 1.94266C38.6583 2.23624 38.5597 2.48165 38.3624 2.6789C38.1698 2.87615 37.9267 2.97477 37.6331 2.97477Z" fill="#303C54" />
              <path d="M40.2964 10.5711V0.25H42.3606V10.5711H40.2964Z" fill="#303C54" />
              <path d="M48.798 3.69037H50.8623V10.5711H48.7912L48.6948 9.95183C48.5251 10.1904 48.3095 10.383 48.0481 10.5298C47.7912 10.6766 47.4907 10.75 47.1467 10.75C46.6329 10.75 46.1513 10.6537 45.7017 10.461C45.2568 10.2683 44.8646 10.0023 44.5251 9.66284C44.1857 9.31881 43.9196 8.92431 43.727 8.47936C43.5389 8.02982 43.4448 7.54817 43.4448 7.0344C43.4448 6.54358 43.5343 6.08486 43.7132 5.65826C43.8921 5.23165 44.1421 4.8578 44.4632 4.5367C44.7889 4.21101 45.1627 3.95642 45.5847 3.77294C46.0114 3.58945 46.4678 3.49771 46.954 3.49771C47.3485 3.49771 47.7063 3.58028 48.0274 3.74541C48.3531 3.90596 48.6421 4.1078 48.8944 4.35092L48.798 3.69037ZM47.0916 8.76835C47.376 8.76835 47.6352 8.69495 47.8692 8.54817C48.1031 8.40138 48.2889 8.20413 48.4265 7.95642C48.5641 7.70872 48.6329 7.43349 48.6329 7.13073C48.6329 6.82798 48.5641 6.55275 48.4265 6.30505C48.2889 6.05734 48.1031 5.86009 47.8692 5.7133C47.6352 5.56651 47.376 5.49312 47.0916 5.49312C46.8072 5.49312 46.548 5.56651 46.3141 5.7133C46.0847 5.86009 45.9013 6.05734 45.7636 6.30505C45.6306 6.55275 45.5641 6.82798 45.5641 7.13073C45.5641 7.43349 45.6329 7.70872 45.7705 7.95642C45.9081 8.20413 46.0916 8.40138 46.321 8.54817C46.5549 8.69495 46.8118 8.76835 47.0916 8.76835Z" fill="#303C54" />
              <path d="M56.9161 0.250001V1.2176H55.6259V4.76548H54.6583V1.2176H53.3682V0.250001H56.9161Z" fill="#303C54" />
              <path d="M57.2391 4.76548L58.1164 0.250001H58.9421L59.9548 2.91413L60.9676 0.250001H61.7933L62.6706 4.76548H61.6836L61.2127 2.32712L60.2903 4.76548H59.6194L58.6969 2.32712L58.226 4.76548H57.2391Z" fill="#303C54" />
            </svg>
          </h1>

          <Button
            data-tour="new-chat"
            onClick={handleNewDiscussion}
            className={clsx({
              "!bg-opacity-90 bg-dark-blue-100 hover:bg-dark-blue-100 cursor-not-allowed":
                isDisabled,
            })}
          >
            <img src={icon} alt="star" />
            New Chat
          </Button>
        </div>

        <div className="w-full flex items-center justify-center" style={{ display: (favorites == null || milaHistoricData == null) ? "in-line" : "none" }}>
          <LoaderCircle className="w-5 h-5 text-white-500 animate-spin" />
        </div>
        <div data-tour="history" className="scrollbar-thin mt-8 overflow-y-auto lg:pr-2 font-notosans w-full block max-h-[88%]">
          {!loadingFavorite && (
            <FavoritesList
              favorites={favorites}
              onFavoriteClick={onFavoriteClick}
              onDelete={onDeleteFavorite}
              onRefresh={onRefreshFavorites}
            />
          )}
          {!loadingFavorite && (
            <MilaHistoricList
              milaHistoric={milaHistoricData}
              onHistoricItemClick={onHistoricItemClick}
              active={threadId || null}
            />
          )}
        </div>
        {/* Sidebar footer — GreenRoad logo with the profile settings gear beside it (design).
            Only where there is no AI services rail: the rail is headed by the same logo and
            carries Settings at its foot, and showing both would be the logo twice on one
            screen and two doors to one modal. */}
        {!hasRail && (
          <>
            <div className="flex-1" />
            <div className="w-full flex items-center justify-between px-4 lg:px-2 lg:pr-6">
              <img
                src={greenRoadLogo}
                alt="GreenRoad"
                className="h-6 w-auto"
              />
              <button
                title="What Mila knows about you"
                aria-label="Settings"
                onClick={onOpenSettings}
                disabled={isDisabled}
                className={clsx(
                  "text-dimgray-200 hover:text-dark-blue-100 transition-colors disabled:cursor-not-allowed p-1 rounded-full",
                  { "animate-pulse text-dark-blue-100 ring-2 ring-[#C4C2F4] bg-[#F3F1FF]": isSettingsHighlighted }
                )}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
              </button>
            </div>
          </>
        )}
      </aside>

      <main className="flex-1 flex flex-col overflow-y-auto md:overflow-y-hidden mx-4 mt-4 rounded-t-3xl bg-white">
        <Menu
          isOpen={isSidebarOpen}
          setIsOpen={() => setIsSidebarOpen((prev) => !prev)}
          className={clsx(
            "fixed lg:!hidden w-9 h-9 z-20 transition-all duration-300 p-1.5",
            "top-8 left-6 sm:left-10",
            { "left-[calc(100%-36px-0.5rem)] sm:!left-[256px]": isSidebarOpen }
          )}
        />
        {/* Only where the shell renders no rail (mobile, or no entitled agents): everywhere
            else the same action lives at the foot of the AI services rail. */}
        {!hasRail && (
          <button
            className={clsx(
              "fixed top-8 right-10 w-[110px] h-[40px] inline-flex items-center justify-center gap-2 shrink-0 rounded-2xl bg-white border-dimgray-200 border text-dark-blue-100 font-notosans text-sl font-semibold transition-[right] disabled:cursor-not-allowed",
              { "sm:right-[372px]": isRightMenuOpen }
            )}
            onClick={onSignOut}
            disabled={isDisabled}
          >
            <img src="../icons/log_out.svg" alt="Log out" />
            {<span>Central</span>}
          </button>
        )}
        <div className="flex flex-col flex-1 min-h-0 max-h-screen pt-[72px] sm:pt-0">
          {landingResolved ? (
            <MilaAiPage
              externalMessage={externalMessage}
              setFavorites={setFavorites}
              favorites={favorites}
              setMilaHistoricData={setMilaHistoricData}
              inputConversation={histoConversation}
              onConversationLoaded={onConversationLoaded}
              onResetDone={onResetDone}
              reset={reset}
              resetRequestId={resetRequestId}
              inputThreadId={threadId}
              onExternalMessageHandled={onExternalMessageHandled}
              onFilesUploaded={handleFilesUploaded}
              onResetUploader={(resetAll) => {
                uploaderResetRef.current = resetAll;
              }}
              setCurrentMessageInBox={setCurrentMessageInBox}
            />
          ) : (
            <div className="flex flex-1 items-center justify-center">
              <LoaderCircle className="w-8 h-8 text-dark-blue-100 animate-spin" />
            </div>
          )}
        </div>
        {/* Thread-scoped: an unfinished onboarding left in history must not strip the
            disclaimer from every other (normal) thread the user opens afterwards. */}
        {!onboarding.isActiveThread(threadId) && (
          <p className="hidden sm:flex w-full justify-center px-4 pb-3 pt-4 sm:px-0 font-notosans text-minimal leading-5 bg-white text-dimgray-200 text-center shrink-0">
            AskMila<>&trade;</> is designed to assist decision-making, but users should independently verify critical information before taking action
          </p>
        )}
      </main>

      {/* Right Section - Insights  */}
      <div data-tour="insights" className="flex fixed top-0 right-0 h-full xl:relative z-20 max-w-[275px] xs:max-w-full">
        <div
          data-tour="insights-toggle"
          className="absolute h-[100px] w-12 top-1/2 -translate-y-1/2 -left-12 z-10 rounded-r-none rounded-full flex items-center justify-center"
        >
          <img
            src={buttonImage}
            alt="button background"
            className="absolute image-orientation-none h-[100px] w-12 z-20"
          />
          <button
            onClick={() => setIsRightMenuOpen((prev) => !prev)}
            className="absolute z-20 !w-12 !h-12"
          >
            <img src={isRightMenuOpen ? ellipse : icon} alt="star" className="!w-[30px] !h-[30px] m-2 relative" />
            <MessageNotification
              onSelected={handleMessageSelected}
              refresh={refreshNotif}
              setScheduledData={updateScheduledData}
              setScheduledStatus={setScheduledStatus}
            />
          </button>
        </div>
        <div
          className={clsx(
            "transition-[width] p-3 pr-0 pl-1 w-0 overflow-hidden bg-white font-notosans mt-4 rounded-tl-3xl shadow-right-menu",
            { "!w-[320px] xs:!w-[330px] !p-3": isRightMenuOpen }
          )}
        >
          <Scheduled
            data={scheduledData}
            status={scheduledStatus}
            onRefreshData={() => setRefreshNotif((prev) => prev + 1)}
            onRead={onHistoricItemClick}
            threadId={threadId}
          />
        </div>
      </div>

      <GrtAnalyticsGA4 onLoaded={handleAnalytics} />
      <SessionWatcher />
      {showTermsModal && <div className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-md flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-2xl max-w-2xl">
          <TermsAcceptanceModal
            onAccept={handleAcceptTerms}
            onCancel={handleCancelTerms}
          />
        </div>
      </div>}
    </div>
  );
};

export default MilaContainer;
